import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Group,
  Indicator,
  Loader,
  Popover,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { IconBell } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiFetch } from '@/api/client';
import { formatDateTime } from '@/lib/format';
import { useApiMutation } from '@/lib/mutations';

import { notificationKeys, useInbox, useUnreadCount } from './api';
import type { InboxItem, UnreadCount } from './types';

/** Cloche de la barre supérieure : alertes internes de l'utilisateur connecté. */
export function NotificationBell() {
  const [opened, setOpened] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const unread = useUnreadCount();
  const inbox = useInbox(opened);
  const count = unread.data?.unread ?? 0;

  const markRead = useApiMutation(
    (item: InboxItem) =>
      apiFetch<InboxItem>(`/notifications/inbox/${item.id}/read`, { method: 'POST' }),
    { invalidate: [notificationKeys.inbox] },
  );
  const markAll = useApiMutation(
    () => apiFetch<UnreadCount>('/notifications/inbox/read-all', { method: 'POST' }),
    { invalidate: [notificationKeys.inbox], success: 'Toutes les notifications sont lues.' },
  );

  const open = (item: InboxItem) => {
    if (!item.read_at) markRead.mutate(item);
    if (item.link) {
      setOpened(false);
      void navigate(item.link);
    }
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      width={360}
      position="bottom-end"
      shadow="md"
      withinPortal
    >
      <Popover.Target>
        <Indicator
          label={count > 99 ? '99+' : count}
          size={16}
          color="red"
          offset={4}
          disabled={count === 0}
        >
          <ActionIcon
            variant="default"
            size="lg"
            aria-label="Notifications"
            onClick={() => {
              if (!opened) void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
              setOpened((o) => !o);
            }}
          >
            <IconBell size={18} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <Group justify="space-between" px="sm" py="xs">
          <Text fw={600} size="sm">
            Notifications {count > 0 && `(${count} non lue${count > 1 ? 's' : ''})`}
          </Text>
          <Button
            size="compact-xs"
            variant="subtle"
            disabled={count === 0}
            loading={markAll.isPending}
            onClick={() => markAll.mutate(undefined)}
          >
            Tout marquer comme lu
          </Button>
        </Group>
        <Divider />
        {inbox.isPending ? (
          <Group justify="center" py="md">
            <Loader size="sm" />
          </Group>
        ) : !inbox.data || inbox.data.results.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            Aucune notification.
          </Text>
        ) : (
          <ScrollArea.Autosize mah={400}>
            <Stack gap={0}>
              {inbox.data.results.map((item) => (
                <UnstyledButton
                  key={item.id}
                  onClick={() => open(item)}
                  px="sm"
                  py="xs"
                  style={{
                    borderBottom: '1px solid var(--mantine-color-default-border)',
                    background: item.read_at ? undefined : 'var(--mantine-primary-color-light)',
                  }}
                >
                  <Group gap="xs" wrap="nowrap" align="flex-start">
                    <Box
                      mt={6}
                      w={8}
                      h={8}
                      style={{
                        borderRadius: 4,
                        flexShrink: 0,
                        background: item.read_at ? 'transparent' : 'var(--mantine-color-red-6)',
                      }}
                      aria-hidden
                    />
                    <div>
                      <Text size="sm" fw={item.read_at ? 400 : 600}>
                        {item.subject || item.event_label}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={3}>
                        {item.body}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {formatDateTime(item.created_at)}
                      </Text>
                    </div>
                  </Group>
                </UnstyledButton>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        )}
      </Popover.Dropdown>
    </Popover>
  );
}
